"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createFeatureFlags = void 0;
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const useDevOverrides_1 = require("./useDevOverrides");
function getDisplayName(WrappedComponent) {
    return WrappedComponent.displayName || WrappedComponent.name || "Component";
}
/**
 * Creates a set of type safe utilities for working with feature flags.
 *
 * @example export const { Feature, FlagsProvider, useFeature, useFeatures, withFeature, behindFeature } = createFeatureFlags({
 *  "test-2": false,
 *  "another-flag": false
 * });
 * @param flagDefaults A object of flag names (keys) and their default values. Values can be overwritten by `FlagsProvider`.
 */
function createFeatureFlags(flagDefaults) {
    const flagContext = (0, react_1.createContext)(flagDefaults);
    const exports = {
        /**
         * Returns a type safe Record of all feature flags based on the default flag values.
         */
        useFeatures() {
            return (0, react_1.useContext)(flagContext);
        },
        /**
         * A type safe hook to read a feature flag value.
         */
        useFeature(name) {
            const features = exports.useFeatures();
            return features[name];
        },
        /**
         * A component that will conditional run UI based on a feature flag state.
         * `render`/`fallback` will take precedence over `children`, when provided.
         */
        Feature(props) {
            const hasFeature = exports.useFeature(props.name);
            if (hasFeature && props.render) {
                return props.render();
            }
            if (!hasFeature && props.fallback) {
                return props.fallback();
            }
            if (typeof props.children === "function") {
                return props.children(hasFeature);
            }
            return (0, jsx_runtime_1.jsx)(jsx_runtime_1.Fragment, { children: hasFeature ? props.children : null });
        },
        /**
         * Dynamically set feature flags, to be used throughout the app.
         * Feature flags are automatically merged with default feature flags.
         */
        FlagsProvider({ features, children, }) {
            const devOverrides = (0, useDevOverrides_1.useDevOverrides)(flagDefaults, (value) => value === "true" || value === "1");
            const combinedFlags = (0, react_1.useMemo)(() => {
                return { ...flagDefaults, ...features, ...devOverrides };
            }, [features, devOverrides]);
            return ((0, jsx_runtime_1.jsx)(flagContext.Provider, { value: combinedFlags, children: children }));
        },
        /**
         * Higher Order Component that adds a feature flag as a prop with same name to wrapped component.
         * TS will error if component doesn't implement a prop with same name as provided feature flag.
         *
         * @example export default withFeature("featureA")(MyComponent);
         * @param featureName Key of the flag to provide as a prop.
         * @see `behindFeature` when there is no existing feature.
         */
        withFeature: (featureName) => function (WrappedComponent) {
            const ComponentWithFeature = (props) => {
                const hasFeature = exports.useFeature(featureName);
                const allProps = { [featureName]: hasFeature, ...props };
                return (0, jsx_runtime_1.jsx)(WrappedComponent, { ...allProps });
            };
            ComponentWithFeature.displayName = `withFeature(${getDisplayName(WrappedComponent)})`;
            return ComponentWithFeature;
        },
        /**
         * Higher Order Component that hides a component behind a feature flag,
         * which means it only renders when feature is enabled. Useful when working
         * on new features where there is no fallback.
         *
         * @example export default behindFeature("featureA")(MyComponent);
         * @param featureName Key of the flag to test if feature is enabled.
         * @param options.FallbackComponent A component to be rendered when feature is disabled.
         * @see `withFeature` when replacing existing feature.
         */
        behindFeature: (featureName) => function (WrappedComponent, options) {
            const ComponentWithFeature = (props) => {
                const hasFeature = exports.useFeature(featureName);
                const Fallback = options?.FallbackComponent;
                if (!hasFeature) {
                    if (Fallback) {
                        return (0, jsx_runtime_1.jsx)(Fallback, { ...props });
                    }
                    return null;
                }
                return (0, jsx_runtime_1.jsx)(WrappedComponent, { ...props });
            };
            ComponentWithFeature.displayName = `behindFeature(${getDisplayName(WrappedComponent)})`;
            return ComponentWithFeature;
        },
    };
    return exports;
}
exports.createFeatureFlags = createFeatureFlags;
